import { CommonModule } from '@angular/common';
import { Component, type OnInit, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import {
  CdwConfirmationDialogComponent,
  type ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  loadAdmins,
  removeAdmin,
} from '../../../../+state/platform-admins/platform-admins.actions';
import type { PlatformAdminListItem } from '../../../../+state/platform-admins/platform-admins.models';
import {
  selectAdminsError,
  selectAdminsLoading,
  selectAdminsMutating,
  selectAllAdmins,
} from '../../../../+state/platform-admins/platform-admins.selectors';
import { selectCurrentAdmin } from '../../../../+state/platform-identity/platform-identity.selectors';
import { AddPlatformAdminComponent } from './add-platform-admin-dialog/add-platform-admin.component';

type SortKey = 'name' | 'email' | 'addedAt';
interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

@Component({
  selector: 'cdw-platform-admins-list',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    CdwButtonModule,
    CdwIconModule,
  ],
  templateUrl: './platform-admins-list.component.html',
  styleUrl: './platform-admins-list.component.scss',
})
export class PlatformAdminsListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);

  private readonly sort$ = new BehaviorSubject<SortState>({
    key: 'name',
    dir: 'asc',
  });

  readonly loading$ = this.store.select(selectAdminsLoading);
  readonly mutating$ = this.store.select(selectAdminsMutating);
  readonly error$ = this.store.select(selectAdminsError);
  readonly currentAdminId$ = this.store
    .select(selectCurrentAdmin)
    .pipe(map((admin) => admin?.profile.id ?? null));

  /** Only supers may add/remove admins; the backend enforces this too. */
  readonly currentAdminIsSuper$ = this.store
    .select(selectCurrentAdmin)
    .pipe(map((admin) => admin?.profile.isSuper ?? false));

  readonly sortState$ = this.sort$.asObservable();

  /** Store list re-sorted client-side per the active sort header. */
  readonly admins$ = combineLatest([
    this.store.select(selectAllAdmins),
    this.sort$,
  ]).pipe(map(([admins, sort]) => this.sortAdmins(admins, sort)));

  ngOnInit(): void {
    this.store.dispatch(loadAdmins());
  }

  toggleSort(key: SortKey): void {
    const current = this.sort$.value;
    this.sort$.next(
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  }

  openAddDialog(): void {
    // The dialog dispatches addAdmin and closes itself on success, so the list
    // updates from the store in place — nothing to do on close.
    this.dialog.open(AddPlatformAdminComponent, {
      width: '480px',
      maxWidth: '100%',
      disableClose: true,
      hasBackdrop: true,
    });
  }

  confirmRemove(admin: PlatformAdminListItem): void {
    this.dialog
      .open(CdwConfirmationDialogComponent, {
        data: {
          title: 'Confirmation',
          message: `Remove ${admin.name} (${admin.email})? This revokes their console access immediately.`,
          type: 'warning',
          layout: 'warning-box',
          confirmText: 'Remove',
          cancelText: 'Cancel',
          confirmButtonColor: 'warn',
          hard: true,
        },
        width: '540px',
        maxWidth: '100%',
        disableClose: true,
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (result?.confirmed) {
          this.store.dispatch(removeAdmin({ id: admin.id }));
        }
      });
  }

  reload(): void {
    this.store.dispatch(loadAdmins());
  }

  trackById(_index: number, admin: PlatformAdminListItem): number {
    return admin.id;
  }

  private sortAdmins(
    admins: PlatformAdminListItem[],
    { key, dir }: SortState
  ): PlatformAdminListItem[] {
    const factor = dir === 'asc' ? 1 : -1;
    return [...admins].sort((a, b) => {
      if (key === 'addedAt') {
        const at = a.addedAt ? Date.parse(a.addedAt) : 0;
        const bt = b.addedAt ? Date.parse(b.addedAt) : 0;
        return (at - bt) * factor;
      }
      return (
        (a[key] ?? '')
          .toString()
          .toLowerCase()
          .localeCompare((b[key] ?? '').toString().toLowerCase()) * factor
      );
    });
  }
}
